import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { EstimateDetail, UpdateEstimateInput, UpdateInspectionInput } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { StatusText } from '../../components/ui/StatusText';
import { StepIndicator } from '../../components/ui/StepIndicator';
import { isConflictError, isRefusedError, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ApplyTemplateDialog } from './ApplyTemplateDialog';
import { clampStep, type EstimatorStep, estimateStatusTone, vehicleTitle } from './estimates-display';
import { EstimatorStep1 } from './EstimatorStep1';
import { EstimatorStep2 } from './EstimatorStep2';
import { EstimatorStep3 } from './EstimatorStep3';
import { type EstimatorForm, formFromDetail, missingIdentityFields, toFactsDraft, toInspectionDraft } from './estimator-form';
import { compileEquations, DEFAULT_TIME_ZONE, evaluateLocally, evaluateQuickRange, todayIso, toEstimateBundle } from './valuation-local';

/**
 * Long enough that a burst of typing is one save, short enough that stepping away is safe. The local
 * engine run is **not** debounced — the number moves with the keystroke; only the write waits.
 */
const SAVE_DEBOUNCE_MS = 400;

/** A save that failed for a reason nobody can act on is tried again on its own, once the line has settled. */
const SAVE_RETRY_MS = 4_000;

type FactsPatch = Omit<UpdateEstimateInput, 'estimateId' | 'expectedRevision'>;
type InspectionPatch = Omit<UpdateInspectionInput, 'owner'>;

/** Everything the page still owes the service, merged field by field as the salesperson types. */
interface PendingPatch {
  facts: FactsPatch | null;
  inspection: InspectionPatch | null;
}

const NOTHING_PENDING: PendingPatch = { facts: null, inspection: null };

const hasWork = (patch: PendingPatch): boolean => patch.facts !== null || patch.inspection !== null;

/**
 * `newer` wins field by field, because it is what was typed last: this is how a save that failed is put
 * back **under** the edits that arrived while it was away, instead of over them (plan §11 fix 7).
 */
const mergePending = (older: PendingPatch, newer: PendingPatch): PendingPatch => ({
  facts: older.facts === null && newer.facts === null ? null : { ...older.facts, ...newer.facts },
  inspection: older.inspection === null && newer.inspection === null ? null : { ...older.inspection, ...newer.inspection },
});

/**
 * The estimator (plan §7).
 *
 * Three things make it work, and all three are the same idea: **the salesperson is talking to a
 * customer, so nothing may wait for the network.**
 *
 *  - The record exists before a single field is typed. `/estimates/new` calls `estimates.create`
 *    immediately and replaces the URL with the real id, so the number on screen is the number in the
 *    database and a half-finished estimate is never lost.
 *  - Every input saves itself, debounced, carrying `expectedRevision`. A second tab editing the same
 *    estimate produces a translated 409 and a refetch rather than a silent overwrite (plan §2.3). A save
 *    that fails for any *other* reason keeps its patch: it goes back on the queue under whatever has
 *    been typed since, the header says "Unsaved", and the write is retried (plan §11 fix 7).
 *  - The appraisal and the quick range are computed **in the browser** by the same engine the service
 *    runs — with the equation version the estimate **cites**, not with whatever is published today
 *    (plan §11 fix 4) — so they move with the keystroke; the server's own result replaces them when the
 *    save returns. If the two ever disagreed, the bug would be in one of the two bundle mappings, which
 *    is exactly why they are written to mirror each other (plan §2.5).
 */
export const EstimatorPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<EstimatorStep>(1);
  const [form, setForm] = useState<EstimatorForm | null>(null);
  const [loadedEstimateId, setLoadedEstimateId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [templateDialogId, setTemplateDialogId] = useState<string | null>(null);

  const detailQuery = useQuery(orpc.estimates.get.queryOptions({ input: { estimateId: id ?? '' }, enabled: Boolean(id) }));
  const detail = detailQuery.data ?? null;

  // The estimate quotes the equation version it was priced with; that is the one the browser has to run,
  // because a publish with scope `NEW` leaves every open estimate on an older one (plan §11 fix 4).
  const citedVersionId = detail?.policy?.versionId ?? null;

  const citedPolicyQuery = useQuery(
    orpc.policies.getVersion.queryOptions({ input: { versionId: citedVersionId ?? '' }, enabled: Boolean(citedVersionId) }),
  );
  // Only to notice that the workspace has moved on: a 404 here (no policy published yet) leaves the page
  // pricing off the cited version as if nothing had happened.
  const publishedPolicyQuery = useQuery(orpc.policies.getPublished.queryOptions());
  const templatesQuery = useQuery(orpc.templates.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' } }));
  const catalogQuery = useQuery(orpc.templates.paramCatalog.queryOptions());

  const templateVersionQuery = useQuery(
    orpc.templates.getVersion.queryOptions({
      input: { versionId: detail?.template?.versionId ?? '' },
      enabled: Boolean(detail?.template?.versionId),
    }),
  );

  const createEstimate = useMutation(orpc.estimates.create.mutationOptions());
  const updateEstimate = useMutation(orpc.estimates.update.mutationOptions());
  const updateInspection = useMutation(orpc.inspections.update.mutationOptions());
  const selectQuickCategory = useMutation(orpc.estimates.selectQuickCategory.mutationOptions());

  // ── creating the record on arrival ──────────────────────────────────────────────────────────────
  const creatingRef = useRef(false);

  /** Stable per estimate id, so the effect below runs once per arrival and the retry button can re-run it. */
  const startEstimate = useCallback((): void => {
    if (id || creatingRef.current) {
      return;
    }

    creatingRef.current = true;
    setCreateError(null);
    const templateId = searchParams.get('templateId');

    createEstimate
      .mutateAsync(templateId ? { templateId } : {})
      .then((created) => {
        navigate(`/estimates/${created.id}`, { replace: true });
      })
      .catch((error: unknown) => {
        creatingRef.current = false;
        setCreateError(serverMessage(error) ?? t(submitErrorKey(error)));
      });
    // `createEstimate` is a fresh object every render, and re-running this on a failed create would
    // spin; the retry is the button on the error panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    startEstimate();
  }, [startEstimate]);

  // ── loading the record into the form ────────────────────────────────────────────────────────────
  const revisionRef = useRef<number>(0);
  /** The highest revision the form has already been filled from. */
  const seenRevisionRef = useRef<number>(0);
  /** Everything typed but not yet written; a failed save merges back into it (plan §11 fix 7). */
  const pendingRef = useRef<PendingPatch>(NOTHING_PENDING);
  /** Exactly one write is in flight; a flush asked for meanwhile is served by the one that is running. */
  const isFlushingRef = useRef(false);
  const flushAgainRef = useRef(false);
  /** The write loop that is in flight, so a second caller can wait for it instead of racing it. */
  const runningFlushRef = useRef<Promise<void> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPending = (): boolean => hasWork(pendingRef.current);

  /**
   * Fill the form from the server **only** when there is something new to fill it from: a different
   * estimate, or a revision the page has not adopted yet. Re-filling on every render of the same data
   * would throw away whatever the salesperson has typed since — which is exactly what a debounced save
   * makes possible, because its response lands mid-sentence.
   */
  useEffect(() => {
    if (!detail) {
      return;
    }

    if (loadedEstimateId === detail.id && (detail.revision <= seenRevisionRef.current || isPending())) {
      revisionRef.current = Math.max(revisionRef.current, detail.revision);

      return;
    }

    seenRevisionRef.current = detail.revision;
    revisionRef.current = detail.revision;
    setLoadedEstimateId(detail.id);
    setForm(formFromDetail(detail));
  }, [detail, loadedEstimateId]);

  /**
   * The server's answer becomes the truth on return (plan §7) — but only when the salesperson has
   * stopped typing. With another edit already queued, the queued edit is newer than this response and
   * adopting it would undo a keystroke.
   */
  const applyDetail = useCallback(
    (next: EstimateDetail) => {
      revisionRef.current = next.revision;
      seenRevisionRef.current = next.revision;
      // The exact key the page's own `useQuery` is built from; a filter-shaped key would write to a
      // different cache entry and leave the page reading the version it started with.
      queryClient.setQueryData(orpc.estimates.get.queryOptions({ input: { estimateId: next.id } }).queryKey, next);
      setSavedAt(Date.now());

      if (!isPending()) {
        setLoadedEstimateId(next.id);
        setForm(formFromDetail(next));
      }
    },
    [queryClient],
  );

  const invalidateLists = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.estimates.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.stats.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.recentEstimates.key() }),
    ]);
  }, [queryClient]);

  /** Somebody else saved first: drop what was queued, take the server's copy and say so (plan §2.3). */
  const onStale = useCallback(async () => {
    pendingRef.current = NOTHING_PENDING;
    setHasPendingEdits(false);
    seenRevisionRef.current = 0;
    setActionError(t('errors.staleRevision'));
    setLoadedEstimateId(null);
    await detailQuery.refetch();
  }, [detailQuery, t]);

  /** Runs the flush on a timer without the flush having to know about the timer. */
  const flushRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const scheduleFlush = useCallback((delayMs: number) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void flushRef.current();
    }, delayMs);
  }, []);

  /**
   * One pass over the queue: the estimate first, then the inspection, so one revision is enough.
   * Answers whether the queue was emptied, which is what tells the caller to keep going.
   */
  const writeOnce = useCallback(async (): Promise<boolean> => {
    if (!id) {
      return false;
    }

    const inFlight = pendingRef.current;

    if (!hasWork(inFlight)) {
      return true;
    }

    pendingRef.current = NOTHING_PENDING;
    setSaveFailure(null);
    setActionError(null);

    try {
      if (inFlight.facts) {
        applyDetail(await updateEstimate.mutateAsync({ estimateId: id, expectedRevision: revisionRef.current, ...inFlight.facts }));
      }

      if (inFlight.inspection) {
        await updateInspection.mutateAsync({ owner: { type: 'ESTIMATE', id }, ...inFlight.inspection });

        const refreshed = await detailQuery.refetch();

        if (refreshed.data) {
          applyDetail(refreshed.data);
        }
      }

      await invalidateLists();
      setHasPendingEdits(isPending());

      return true;
    } catch (error: unknown) {
      if (isConflictError(error)) {
        // The only failure allowed to throw work away: the record moved on, so the queued answer is to a
        // question that has changed.
        await onStale();

        return false;
      }

      // Everything else is a bad moment, not a verdict. The patch goes back **under** whatever has been
      // typed since, so no keystroke is lost and the newer value still wins (plan §11 fix 7).
      pendingRef.current = mergePending(inFlight, pendingRef.current);
      setHasPendingEdits(true);

      const key = submitErrorKey(error);
      const explanation = serverMessage(error) ?? (key === 'errors.saveFailed' ? null : t(key));

      setSaveFailure([t('estimates.saveFailed'), explanation].filter((part) => part !== null).join(' '));

      // A refusal will be refused again in four seconds' time, so only something that could have been
      // the line tries again on its own. Every other `4xx` waits for the person: a half-typed model
      // year the contract rejects used to be re-sent every four seconds for as long as the page was
      // open, and the next keystroke queues the patch again anyway.
      if (!isRefusedError(error)) {
        scheduleFlush(SAVE_RETRY_MS);
      }

      return false;
    }
  }, [applyDetail, detailQuery, id, invalidateLists, onStale, scheduleFlush, t, updateEstimate, updateInspection]);

  /** Writes whatever is pending, one write at a time, until the queue is empty or a write fails. */
  const flush = useCallback(async (): Promise<void> => {
    if (isFlushingRef.current) {
      // The running loop picks the extra work up on its next turn. Waiting for it here is what makes
      // `savePending` honest: an outcome that asked for the revision while a debounced write was still
      // in flight would otherwise be handed the revision from *before* that write, and record its offer
      // against a row the service had already moved on from — a 409 for a save nobody raced.
      flushAgainRef.current = true;
      await runningFlushRef.current;

      return;
    }

    if (!id || !hasWork(pendingRef.current)) {
      return;
    }

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    isFlushingRef.current = true;
    setIsSaving(true);

    const running = (async () => {
      try {
        let wrote = true;

        do {
          flushAgainRef.current = false;
          wrote = await writeOnce();
        } while (wrote && flushAgainRef.current && hasWork(pendingRef.current));
      } finally {
        isFlushingRef.current = false;
        runningFlushRef.current = null;
        setIsSaving(false);
      }
    })();

    runningFlushRef.current = running;
    await running;
  }, [id, writeOnce]);

  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  /**
   * What an outcome has to do before it writes: empty the queue and say which revision the record is on
   * afterwards. Step 3 records the offer with it, so an approval is never asked for on top of a
   * debounced save — the 409 the outcome would otherwise be answered with, and a snapshot bound to a
   * vehicle missing the last thing typed (plan §5.6).
   */
  const savePending = useCallback(async (): Promise<number> => {
    await flush();

    return revisionRef.current;
  }, [flush]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      // Leaving the estimator is not a reason to lose a keystroke: the unmount writes what is queued
      // (plan §11 fix 7). `beforeunload` below covers the exits the router never sees.
      void flushRef.current();
    },
    [],
  );

  useEffect(() => {
    if (!hasPendingEdits && !isSaving) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasPendingEdits, isSaving]);

  /** An in-app exit: write first, and only ask when something could not be written after all. */
  const leaveTo = useCallback(
    async (path: string): Promise<void> => {
      await flush();

      if (isPending() && !window.confirm(t('estimates.unsavedConfirm'))) {
        return;
      }

      navigate(path);
    },
    [flush, navigate, t],
  );

  const changeForm = useCallback(
    (patch: Partial<EstimatorForm>, save: FactsPatch | null, inspectionSave: InspectionPatch | null = null) => {
      setForm((current) => (current === null ? current : { ...current, ...patch }));

      if (save || inspectionSave) {
        pendingRef.current = mergePending(pendingRef.current, { facts: save, inspection: inspectionSave });
        setHasPendingEdits(true);
        scheduleFlush(SAVE_DEBOUNCE_MS);
      }
    },
    [scheduleFlush],
  );

  // ── the local run ───────────────────────────────────────────────────────────────────────────────
  /** What the estimate is priced with: the version it cites, or the published one before it cites any. */
  const pricingPolicy = (citedVersionId === null ? publishedPolicyQuery.data : citedPolicyQuery.data) ?? null;

  const compiled = useMemo(() => (pricingPolicy ? compileEquations(pricingPolicy.equations) : null), [pricingPolicy]);

  const templateValues = templateVersionQuery.data ?? null;
  const timeZone = user?.tenantTimezone ?? DEFAULT_TIME_ZONE;

  const local = useMemo(() => {
    if (!compiled || !form || !detail) {
      return null;
    }

    const bundle = toEstimateBundle({
      facts: toFactsDraft(form, detail),
      inspection: toInspectionDraft(form),
      overrides: detail.paramOverrides,
      templateVersion: templateValues,
      today: todayIso(timeZone),
    });

    return { valuation: evaluateLocally(compiled, bundle), quickRange: evaluateQuickRange(compiled, bundle) };
  }, [compiled, detail, form, templateValues, timeZone]);

  const onSelectScenario = useCallback(
    async (key: string | null): Promise<void> => {
      if (!id) {
        return;
      }

      setActionError(null);

      try {
        const next = key === null ? null : (key as EstimateDetail['quickCategory']);

        applyDetail(await selectQuickCategory.mutateAsync({ estimateId: id, expectedRevision: revisionRef.current, quickCategory: next }));
        await invalidateLists();
      } catch (error: unknown) {
        if (isConflictError(error)) {
          await onStale();

          return;
        }

        setActionError(serverMessage(error) ?? t(submitErrorKey(error)));
      }
    },
    [applyDetail, id, invalidateLists, onStale, selectQuickCategory, t],
  );

  /**
   * The record moved because of something that is not a keystroke — an offer, a cost item, a purchase.
   * The queue is dropped on purpose: whatever was typed describes a version of the estimate the outcome
   * has already replaced, and re-applying it would undo the outcome (plan §5.6).
   */
  const reloadRecord = useCallback(async (): Promise<void> => {
    pendingRef.current = NOTHING_PENDING;
    setHasPendingEdits(false);
    seenRevisionRef.current = 0;
    setLoadedEstimateId(null);

    const refreshed = await detailQuery.refetch();

    if (refreshed.data) {
      applyDetail(refreshed.data);
    }
  }, [applyDetail, detailQuery]);

  const onTemplateApplied = useCallback(
    async (next: EstimateDetail): Promise<void> => {
      setTemplateDialogId(null);
      // A template application rewrites make, model, fuel and the reference price, so the form is
      // refilled from the server's answer whatever the page happens to be holding.
      pendingRef.current = NOTHING_PENDING;
      setHasPendingEdits(false);
      seenRevisionRef.current = 0;
      applyDetail(next);
      await Promise.all([invalidateLists(), queryClient.invalidateQueries({ queryKey: orpc.templates.getVersion.key() })]);
    },
    [applyDetail, invalidateLists, queryClient],
  );

  if (!id) {
    // A create that failed leaves nothing to spin over: the page says so and offers the call again.
    return createError === null ? (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('estimates.creating')}
      </div>
    ) : (
      <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
        <h1 className="text-base font-semibold text-destructive">{t('estimates.createFailedTitle')}</h1>
        <p className="mt-2 text-sm text-destructive">{t('estimates.createFailedBody')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{createError}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            isLoading={createEstimate.isPending}
            onClick={() => startEstimate()}
          >
            {t('actions.retry')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/estimates')}
          >
            <ClipboardList className="size-3.5" />
            {t('estimates.backToList')}
          </Button>
        </div>
      </section>
    );
  }

  // The error check comes **before** the loading check: a load that failed has no data either, and
  // testing for data first left the page spinning for ever (plan §11 fix 7).
  if (detailQuery.isError) {
    return (
      <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
        <h1 className="text-base font-semibold text-destructive">{t('estimates.loadFailedTitle')}</h1>
        <p className="mt-2 text-sm text-destructive">{t('estimates.loadFailedBody')}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            isLoading={detailQuery.isFetching}
            onClick={() => void detailQuery.refetch()}
          >
            {t('actions.retry')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/estimates')}
          >
            <ClipboardList className="size-3.5" />
            {t('estimates.backToList')}
          </Button>
        </div>
      </section>
    );
  }

  if (detailQuery.isLoading || !detail || !form) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  const publishedPolicy = publishedPolicyQuery.data ?? null;
  const isPricedWithOlderPolicy = pricingPolicy !== null && publishedPolicy !== null && pricingPolicy.id !== publishedPolicy.id;

  // Step 1 cannot be left until the vehicle is identified (business review 2026-09-23, C6). A closed
  // estimate is read, not filled in, so an old record with a gap can still be opened on every step.
  const isEditable = detail.status !== 'DEAL_AGREED' && detail.status !== 'NO_DEAL';
  const missingIdentity = isEditable ? missingIdentityFields(form, detail) : [];
  const isContinueBlocked = step === 1 && missingIdentity.length > 0;

  const subline = [detail.customer?.name ?? t('estimates.customerNotRecorded'), vehicleTitle(detail)].filter((part) => part !== '').join(' · ');

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-mono text-2xl font-semibold text-foreground">{detail.estimateNo}</h1>
          <p className="mt-1 truncate text-sm text-muted-foreground">{subline}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isSaving ? (
            <Badge>
              <Spinner className="size-3" />
              {t('estimates.saving')}
            </Badge>
          ) : hasPendingEdits ? (
            <Badge tone="warning">{t('estimates.unsaved')}</Badge>
          ) : savedAt !== null ? (
            <Badge tone="success">{t('estimates.saved')}</Badge>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void leaveTo('/estimates')}
          >
            <ClipboardList className="size-3.5" />
            {t('estimates.backToList')}
          </Button>
        </div>
      </header>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <StatusText
              label={t(`estimates.statuses.${detail.status}`)}
              tone={estimateStatusTone(detail.status)}
            />
            {detail.template ? (
              <Badge mono>
                {detail.template.name} v{detail.template.version}
              </Badge>
            ) : null}
            {pricingPolicy ? <Badge mono>{t('estimates.equationsVersion', { version: pricingPolicy.version })}</Badge> : null}
          </div>
          <span className="text-sm text-muted-foreground">{t('estimates.notInInventory')}</span>
        </div>

        {isPricedWithOlderPolicy ? (
          <p className="text-sm text-muted-foreground">
            {t('estimates.pricedWithCitedPolicy', { cited: pricingPolicy.version, published: publishedPolicy.version })}
          </p>
        ) : null}
      </div>

      {saveFailure !== null ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <span>{saveFailure}</span>
          <Button
            variant="outline"
            size="sm"
            isLoading={isSaving}
            onClick={() => void flush()}
          >
            {t('estimates.retrySave')}
          </Button>
        </div>
      ) : null}

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      <StepIndicator
        labels={[t('estimates.step1'), t('estimates.step2'), t('estimates.step3')]}
        current={step - 1}
      />

      {step === 1 ? (
        <EstimatorStep1
          detail={detail}
          form={form}
          local={local}
          templates={templatesQuery.data?.items ?? []}
          templateValues={templateValues}
          onChange={changeForm}
          onPickTemplate={setTemplateDialogId}
          onSelectScenario={(key) => void onSelectScenario(key)}
          missingFields={missingIdentity}
        />
      ) : step === 2 ? (
        <EstimatorStep2
          detail={detail}
          form={form}
          local={local}
          templateValues={templateValues}
          damageOptions={catalogQuery.data ?? null}
          onChange={changeForm}
          onCostsChanged={() => void reloadRecord()}
        />
      ) : (
        <EstimatorStep3
          detail={detail}
          local={local}
          today={todayIso(timeZone)}
          askingPrice={form.customerAskingPrice}
          onChange={(patch, save) => changeForm(patch as Partial<EstimatorForm>, save)}
          onRecordChanged={reloadRecord}
          onSavePending={savePending}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="outline"
          disabled={step === 1}
          onClick={() => setStep(clampStep(step - 1))}
        >
          <ArrowLeft className="size-4 rtl:rotate-180" />
          {t('actions.previous')}
        </Button>
        {step < 3 ? (
          <div className="flex flex-col items-end gap-1">
            <Button
              disabled={isContinueBlocked}
              aria-describedby={isContinueBlocked ? 'estimate-continue-hint' : undefined}
              onClick={() => setStep(clampStep(step + 1))}
            >
              {t('estimates.continue')}
              <ArrowRight className="size-4 rtl:rotate-180" />
            </Button>
            {isContinueBlocked ? (
              <p
                id="estimate-continue-hint"
                className="max-w-md text-end text-xs text-muted-foreground"
              >
                {t('estimates.continueNeeds', {
                  fields: missingIdentity.map((field) => t(`estimates.fields.${field}`)).join(t('common.listSeparator')),
                })}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <ApplyTemplateDialog
        estimateId={detail.id}
        revision={detail.revision}
        templateId={templateDialogId}
        onClose={() => setTemplateDialogId(null)}
        onApplied={(next) => void onTemplateApplied(next)}
      />
    </div>
  );
};
